import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";

import { adminApi } from "@/lib/api";
import { AdminInput, AdminTextarea, EmptyState, FieldLabel } from "@/components/admin/AdminUI";
import ImageUploadField from "@/components/admin/ImageUploadField";

const PAGE_ORDER = ["home", "about", "residential", "bim", "contact"];

export default function AdminSeo() {
  const [pages, setPages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    adminApi.listPages().then((docs) => {
      const sorted = [...docs].sort((a, b) => PAGE_ORDER.indexOf(a.slug) - PAGE_ORDER.indexOf(b.slug));
      setPages(sorted);
    });
    adminApi.listProjects().then(setProjects);
  }, []);

  const savePage = async (p) => {
    setSavingId(`page-${p.slug}`);
    try {
      await adminApi.updatePage(p.slug, { seo: p.seo });
      toast.success("SEO saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingId(null); }
  };

  const saveProject = async (proj) => {
    setSavingId(`proj-${proj.slug}`);
    try {
      await adminApi.updateProject(proj.slug, { seo: proj.seo });
      toast.success("SEO saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingId(null); }
  };

  return (
    <div className="space-y-8">
      <section>
        <p className="eyebrow-muted">[ Pages ]</p>
        <h2 className="text-xl font-semibold mt-2">SEO for public pages</h2>
        <div className="mt-4 space-y-4">
          {pages.map((p) => (
            <div key={p.slug} className="admin-card rounded-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="eyebrow-muted uppercase">{p.slug}</p>
                  <p className="text-lg font-medium mt-1">{p.title}</p>
                </div>
                <button type="button" onClick={() => savePage(p)} disabled={savingId === `page-${p.slug}`} className="btn-primary text-[10px] px-3 py-2 disabled:opacity-60"><Save className="h-3.5 w-3.5" /> Save</button>
              </div>
              <div className="grid md:grid-cols-2 gap-3 mt-4">
                <label><FieldLabel>Title</FieldLabel><AdminInput value={p.seo?.title || ""} onChange={(e) => setPages((prev) => prev.map((x) => x.slug === p.slug ? { ...x, seo: { ...(x.seo || {}), title: e.target.value } } : x))} /></label>
                <div><FieldLabel>OG Image</FieldLabel><ImageUploadField value={p.seo?.og_image || ""} category="general" onChange={(value) => setPages((prev) => prev.map((x) => x.slug === p.slug ? { ...x, seo: { ...(x.seo || {}), og_image: value } } : x))} /></div>
                <label className="md:col-span-2"><FieldLabel>Description</FieldLabel><AdminTextarea rows={2} value={p.seo?.description || ""} onChange={(e) => setPages((prev) => prev.map((x) => x.slug === p.slug ? { ...x, seo: { ...(x.seo || {}), description: e.target.value } } : x))} /></label>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <p className="eyebrow-muted">[ Projects ]</p>
        <h2 className="text-xl font-semibold mt-2">SEO for residential projects</h2>
        <div className="mt-4 space-y-4">
          {projects.length === 0 && <EmptyState title="No projects to configure yet" />}
          {projects.map((p) => (
            <div key={p.slug} className="admin-card rounded-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="eyebrow-muted uppercase">/residential-design/{p.slug}</p>
                  <p className="text-lg font-medium mt-1">{p.title}</p>
                </div>
                <button type="button" onClick={() => saveProject(p)} disabled={savingId === `proj-${p.slug}`} className="btn-primary text-[10px] px-3 py-2 disabled:opacity-60"><Save className="h-3.5 w-3.5" /> Save</button>
              </div>
              <div className="grid md:grid-cols-2 gap-3 mt-4">
                <label><FieldLabel>Title</FieldLabel><AdminInput value={p.seo?.title || ""} onChange={(e) => setProjects((prev) => prev.map((x) => x.slug === p.slug ? { ...x, seo: { ...(x.seo || {}), title: e.target.value } } : x))} /></label>
                <div><FieldLabel>OG Image</FieldLabel><ImageUploadField value={p.seo?.og_image || ""} category="general" onChange={(value) => setProjects((prev) => prev.map((x) => x.slug === p.slug ? { ...x, seo: { ...(x.seo || {}), og_image: value } } : x))} /></div>
                <label className="md:col-span-2"><FieldLabel>Description</FieldLabel><AdminTextarea rows={2} value={p.seo?.description || ""} onChange={(e) => setProjects((prev) => prev.map((x) => x.slug === p.slug ? { ...x, seo: { ...(x.seo || {}), description: e.target.value } } : x))} /></label>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
